import { VulnerabilityResourceInsightRequest as GeneratedVulnerabilityResourceInsightRequest } from '@ariksa/scan-vault/api';
import type { GetVulnerabilityResourceCountSummarySummaryTypeEnum } from '@ariksa/scan-vault/api';
import { v4 as uuidv4 } from 'uuid';

import { scanVaultCvssV3 } from 'api/scan-vault/cvss';
import { ScanVaultService } from 'api/services/services';

export interface VulnerabilityBucket {
  key: string;
  finding_count: number;
  resource_count: number;
}

export interface VulnerabilityTrend {
  day: string;
  severity: string;
  opened: number;
  closed: number;
  delta: number;
}

export interface VulnerabilityPackage {
  ecosystem: string;
  name: string;
  version: string;
  severity: string;
  severity_counts?: Record<string, number>;
  cve_ids: string[];
  fixed_versions?: string[];
  provider_counts?: Record<string, number>;
  patchable?: boolean;
  affected_resource_count: number;
  last_seen_at?: string;
}

export interface VulnerabilityExposureBucket {
  severity: string;
  internet_exposed: number;
  known_exploited: number;
  exploitable: number;
}

export interface VulnerabilityAgeBucket {
  bucket: string;
  finding_count: number;
  resource_count: number;
}

export interface VulnerabilityDashboardResponse {
  as_of: string;
  severity: VulnerabilityBucket[];
  sources: VulnerabilityBucket[];
  trends: VulnerabilityTrend[];
  patchable_packages: VulnerabilityPackage[];
  exposure: VulnerabilityExposureBucket[];
  age: VulnerabilityAgeBucket[];
  source_severity?: VulnerabilitySourceBucket[];
  resource_trends?: VulnerabilityResourceTrend[];
  prioritization?: VulnerabilityPrioritization;
  vulnerable_images?: VulnerabilityImageBucket[];
  coverage?: {
    start?: string;
    end?: string;
    complete: boolean;
  };
}

export interface ScanVaultResourceCountSummary {
  items: Record<string, number>;
}

export interface VulnerabilitySourceBucket extends VulnerabilityBucket {
  severity_counts: Record<string, number>;
}

export interface VulnerabilityResourceTrend {
  day: string;
  resource_type: string;
  severity: string;
  vulnerable_resource_count: number;
  opened_resource_count: number;
  closed_resource_count: number;
  coverage_complete?: boolean;
}

export interface VulnerabilityPrioritization {
  vulnerable_packages: number;
  internet_exposed_resource_packages: number;
  internet_exposed_resource_packages_with_exploitable_cve: number;
  internet_exposed_resource_patchable_packages_with_exploitable_cve: number;
  internet_exposed_resources_having_patchable_packages_with_exploitable_cve: number;
  host_types?: Array<{
    resource_type: string;
    resource_count: number;
  }>;
}

export interface VulnerabilityImageBucket {
  image: string;
  repository_uri?: string;
  image_digest?: string;
  image_count: number;
  resource_count: number;
  severity_counts?: Record<string, number>;
}

export interface VulnerabilityDashboardParams {
  environmentId?: string;
  accountUuid?: string;
  accountId?: string | string[];
  resourceTypes?: string[];
  from?: string;
  to?: string;
  startDate?: string;
  endDate?: string;
  steps?: string[];
}

export interface VulnerabilityOverviewResponse {
  as_of: string;
  top_new: Array<Record<string, any>>;
  highest_occurring: Array<Record<string, any>>;
  most_widely_deployed: Array<Record<string, any>>;
  critical_high: {
    critical: number;
    high: number;
    new: number;
    resolved: number;
  };
  resource_counts: Array<{
    resource_type: string;
    vulnerability_count: Record<string, number>;
    current_count: number;
    previous_count: number;
  }>;
}

export interface ScanVaultFinding {
  finding_id: string;
  resource_uuid: string;
  finding_type: string;
  rule_id: string;
  package_name?: string;
  installed_version?: string;
  ecosystem?: string;
  severity: string;
  package_category: string;
  fixed_version: string;
  patchable: boolean;
  known_exploited: boolean;
  exploitable: boolean;
  detector: string;
  title: string;
  first_seen_at: string;
  last_seen_at: string;
  status?: 'open' | 'resolved';
  opened_at?: string;
  resolved_at?: string;
}

export interface ScanVaultCVE {
  cve_id: string;
  severity: string;
  cvss_score: number;
  cvss_vector: string;
  cvss?: Record<string, string>;
  title?: string;
  description?: string;
  remediation?: string;
  fixed_version: string;
  patchable: boolean;
  known_exploited: boolean;
  exploitable: boolean;
  affected_resource_count: number;
  last_seen_at: string;
  first_seen_at?: string;
  package_name?: string;
  package_version?: string;
  published_date?: string;
  last_modified_date?: string;
  data_source?: Record<string, string>;
  references?: string[];
  cwe_ids?: string[];
}

export interface ScanVaultPackage {
  ecosystem: string;
  name: string;
  version: string;
  severity: string;
  severity_counts?: Record<string, number>;
  cve_ids: string[];
  fixed_versions?: string[];
  provider_counts?: Record<string, number>;
  patchable?: boolean;
  affected_resource_count: number;
  last_seen_at: string;
}

export interface ScanVaultPage<T> {
  items: T[];
  next_cursor?: string;
  total_count?: number | null;
  count_mode?: string;
}

export interface VulnerabilityResourceInsightRequest {
  resource_uuids?: string[];
  resource_ids?: string[];
  resource_types?: string[];
  account_ids?: string[];
  environment_ids?: string[];
  regions?: string[];
  search?: string;
  severities?: string[];
  package_categories?: string[];
  cve_ids?: string[];
  deployed_in?: string;
  patchable?: boolean;
  known_exploited?: boolean;
  exploitable?: boolean;
  internet_exposed?: boolean;
  status?: 'open' | 'resolved';
  from?: string;
  to?: string;
  page?: number;
  size?: number;
  cursor?: string;
}

export interface VulnerabilityResourceInsightItem {
  resource_uuid: string;
  resource_type: string;
  native_id: string;
  cloud: string;
  account_native_id: string;
  account_uuid: string;
  environment_id: string;
  region: string;
  name: string;
  arn: string;
  console_url: string;
  resource_group: string;
  tags: Record<string, string>;
  attrs: Record<string, string>;
  first_seen_at: string;
  last_seen_at: string;
  vulnerabilities: Record<string, number>;
  applications: string[];
  vulnerability_last_seen_at: string;
  finding_count: number;
  status: 'open' | 'resolved';
}

export interface VulnerabilityResourceInsightResponse {
  items: VulnerabilityResourceInsightItem[];
  next_cursor?: string;
  total_count?: number | null;
  count_mode: string;
}

export interface ScanVaultFact {
  fact_kind: string;
  fact_type: string;
  detector?: string;
  resource_uuid: string;
  fact_key: number;
  first_seen_at: string;
  last_seen_at: string;
  content_hash: number;
  data: Record<string, unknown>;
}

export interface ScanVaultLoginActivity {
  username?: string | null;
  host?: string | null;
  login_time?: string | null;
  logout_time?: string | null;
  number_of_attempts?: number | null;
  last_attempt_time?: string | null;
}

export interface ScanVaultResourceFindingPage {
  items: ScanVaultFinding[];
  next_cursor?: string;
  total_count?: number | null;
  count_mode: string;
}

export interface ScanVaultScannerStatus {
  id: string;
  account_uuid: string;
  region?: string;
  zone?: string;
  scanner_id: string;
  version?: string;
  first_seen?: string;
  last_seen?: string;
  state: string;
  settings?: Record<string, unknown>;
  is_in_build?: boolean;
  updated_at?: string;
}

/** Neutral UI model for vulnerability scanner state returned by ScanVault. */
export interface ScanVaultScannerInfo {
  id: string;
  cloud_account_id?: string | null;
  scanner_id?: string | null;
  region?: string | null;
  zone?: string | null;
  version?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  status?: 'Active' | 'Inactive' | 'Deleted' | string;
  current_scan?: string[] | null;
  settings?: Record<string, any> | null;
}

/** Neutral model for the legacy dashboard's SSH summary row. */
export interface ScanVaultSSHSummary {
  name: string;
  port?: number;
  failed_logins: number;
  malicious_egress: boolean;
  cloud: string;
}

export interface ScanVaultScannerAdminResponse {
  settings: Record<string, unknown>;
  statuses: ScanVaultScannerStatus[];
}

export interface ScanVaultCentralAccount {
  id: string;
  org: string;
  account_id: string;
  provider: string;
  effective_enabled_regions: string[];
  created_at: string;
  status: 'Active' | 'Inactive';
  last_scan_at?: string | null;
}

export interface ScanVaultCrossAccountsResponse {
  scanner_account_uuid: string;
  cross_accounts: string[];
}

export const ScanVaultCentralAccountStatus = {
  Active: 'Active',
  Inactive: 'Inactive',
} as const;

export async function getScanVaultVulnerabilityResourceInsight(
  request: VulnerabilityResourceInsightRequest,
): Promise<VulnerabilityResourceInsightResponse> {
  const response = await ScanVaultService.Vulnerability.queryVulnerabilityResourceInsight(
    {
      vulnerabilityResourceInsightRequest: request as GeneratedVulnerabilityResourceInsightRequest,
    },
  );
  return (response.data as unknown) as VulnerabilityResourceInsightResponse;
}

export async function getScanVaultVulnerabilityOverview(
  input: VulnerabilityDashboardParams & { resourceTypes?: string[] } = {},
) {
  const accounts = input.accountUuid
    ? [input.accountUuid]
    : input.accountId
    ? Array.isArray(input.accountId)
      ? input.accountId
      : [input.accountId]
    : undefined;
  const from = input.from ?? dateBoundary(input.startDate, false);
  const to = input.to ?? dateBoundary(input.endDate, true);
  const response = await ScanVaultService.Vulnerability.getVulnerabilityOverview(
    {
      environmentId: input.environmentId ? [input.environmentId] : undefined,
      accountUuid: accounts,
      resourceType: input.resourceTypes,
      from,
      to,
    },
  );
  return readResponseData<VulnerabilityOverviewResponse>(
    response,
    'vulnerability overview',
  );
}

export async function getScanVaultResourceCountSummary(
  input: {
    environmentId?: string;
    accountIds?: string[];
    resourceTypes?: string[];
    summaryType?: string;
  } = {},
): Promise<ScanVaultResourceCountSummary> {
  const response = await ScanVaultService.Vulnerability.getVulnerabilityResourceCountSummary(
    {
      environmentId: input.environmentId ? [input.environmentId] : undefined,
      accountUuid: input.accountIds,
      resourceType: input.resourceTypes,
      summaryType: input.summaryType as
        | GetVulnerabilityResourceCountSummarySummaryTypeEnum
        | undefined,
    },
  );
  return readResponseData<ScanVaultResourceCountSummary>(
    response,
    'resource count summary',
  );
}

export function mapScanVaultOverviewItem(item: Record<string, any>) {
  return {
    vulnerability_id: item.vulnerability_id,
    package_name: item.package_name,
    cvss_score: item.cvss_score,
    occurrences: item.occurrences,
    last_seen: item.last_seen,
    patch_available: item.patch_available,
    patch_version: item.patch_version,
  };
}

export function mapScanVaultOverviewCount(
  response: VulnerabilityOverviewResponse,
  resourceType?: string,
) {
  const values = resourceType
    ? response.resource_counts.filter(item =>
        item.resource_type.toLowerCase().includes(resourceType.toLowerCase()),
      )
    : response.resource_counts;
  const vulnerabilityCount = values.reduce<Record<string, number>>(
    (result, item) => {
      Object.entries(item.vulnerability_count).forEach(([severity, count]) => {
        result[severity] = (result[severity] ?? 0) + count;
      });
      return result;
    },
    {},
  );
  return {
    vulnerability_count: vulnerabilityCount,
    current_count: values.reduce(
      (total, item) => total + item.current_count,
      0,
    ),
    previous_count: values.reduce(
      (total, item) => total + item.previous_count,
      0,
    ),
    percentage_change: null,
  };
}

export async function resolveScanVaultResourceIdentity(input: {
  nativeId: string;
  accountId?: string;
  region?: string;
  resourceType?: string;
}): Promise<{ resource_uuid: string }> {
  const response = await ScanVaultService.Vulnerability.resolveResourceIdentity(
    {
      nativeId: input.nativeId,
      accountId: input.accountId,
      region: input.region,
      resourceType: input.resourceType,
    },
  );
  return readResponseData<{ resource_uuid: string }>(
    response,
    'resource identity',
  );
}

export async function triggerScanVaultScan(input: {
  resourceUUID?: string;
  nativeId?: string;
  accountId?: string;
  region?: string;
  resourceType?: string;
  scannerId?: string;
  detectors?: string[];
}) {
  const resourceUUID =
    input.resourceUUID ??
    (
      await resolveScanVaultResourceIdentity({
        nativeId: input.nativeId ?? '',
        accountId: input.accountId,
        region: input.region,
        resourceType: input.resourceType,
      })
    ).resource_uuid;
  const response = await ScanVaultService.Vulnerability.triggerScan({
    idempotencyKey: uuidv4(),
    createScanRequest: {
      resource_uuid: resourceUUID,
      scanner_id: input.scannerId ?? '',
      detectors: input.detectors ?? [],
    },
  });
  return readResponseData<{
    scan_job_id: string;
    status: string;
    resource_uuid?: string;
  }>(response, 'scan request');
}

export async function getScanVaultScannerInfo(): Promise<
  Record<string, ScanVaultScannerInfo[]>
> {
  const response = await ScanVaultService.Vulnerability.getScannerAdministration();
  const data = readResponseData<ScanVaultScannerAdminResponse>(
    response,
    'scanner information',
  );
  return data.statuses.reduce<Record<string, ScanVaultScannerInfo[]>>(
    (result, status) => {
      const accountID = status.account_uuid;
      (result[accountID] ??= []).push({
        id: status.id,
        cloud_account_id: accountID,
        scanner_id: status.scanner_id,
        region: status.region,
        zone: status.zone,
        version: status.version,
        first_seen: status.first_seen,
        last_seen: status.last_seen,
        status: /ready|active|running/i.test(status.state)
          ? 'Active'
          : 'Inactive',
        current_scan: [],
        settings: status.settings ?? {},
      });
      return result;
    },
    {},
  );
}

export async function getScanVaultCentralScanningAccounts(): Promise<
  ScanVaultCentralAccount[]
> {
  const response = await ScanVaultService.Vulnerability.listCrossAccountMaps();
  const data = readResponseData<{ items: Array<Record<string, any>> }>(
    response,
    'central scanning accounts',
  );
  const grouped = new Map<string, ScanVaultCentralAccount>();
  data.items.forEach(mapping => {
    const id = String(mapping.scanner_account_uuid);
    const current = grouped.get(id);
    if (current) {
      if (mapping.enabled) current.status = 'Active';
      return;
    }
    grouped.set(id, {
      id,
      org: '',
      account_id: id,
      provider: 'AWS',
      effective_enabled_regions: [],
      created_at: mapping.updated_at ?? new Date(0).toISOString(),
      status: mapping.enabled ? 'Active' : 'Inactive',
      last_scan_at: null,
    });
  });
  return Promise.all(
    Array.from(grouped.values()).map(async account => {
      try {
        const regions = await getScanVaultEnabledRegions({
          provider: account.provider,
          accountUuid: account.id,
        });
        return { ...account, effective_enabled_regions: regions.regions ?? [] };
      } catch {
        return account;
      }
    }),
  );
}

export async function getScanVaultCrossAccounts(
  accountUuid: string,
): Promise<ScanVaultCrossAccountsResponse> {
  const response = await ScanVaultService.Vulnerability.listCrossAccountMaps();
  const data = readResponseData<{ items: Array<Record<string, any>> }>(
    response,
    'cross-account mappings',
  );
  return {
    scanner_account_uuid: accountUuid,
    cross_accounts: data.items
      .filter(
        mapping =>
          String(mapping.scanner_account_uuid) === accountUuid &&
          mapping.enabled,
      )
      .map(mapping => String(mapping.cross_account_uuid)),
  };
}

export async function updateScanVaultCentralScanningAccountStatus(input: {
  accountId: string;
  enabled: boolean;
}) {
  const response = await ScanVaultService.Vulnerability.listCrossAccountMaps();
  const data = readResponseData<{ items: Array<Record<string, any>> }>(
    response,
    'central scanning accounts',
  );
  const mappings = data.items.filter(
    mapping => String(mapping.scanner_account_uuid) === input.accountId,
  );
  await Promise.all(
    mappings.map(mapping =>
      ScanVaultService.Vulnerability.updateCrossAccountMap({
        mappingId: String(mapping.id),
        crossAccountMappingRequest: {
          scanner_account_uuid: String(mapping.scanner_account_uuid),
          cross_account_uuid: String(mapping.cross_account_uuid),
          role_arn: mapping.role_arn ? String(mapping.role_arn) : undefined,
          enabled: input.enabled,
        },
      }),
    ),
  );
  return { status: input.enabled ? 'Active' : 'Inactive' };
}

export async function getScanVaultEnabledRegions(input: {
  provider: string;
  accountUuid?: string;
}) {
  const response = await ScanVaultService.Vulnerability.getEnabledRegions({
    provider: input.provider,
    accountUuid: input.accountUuid,
  });
  return readResponseData<{
    provider: string;
    account_uuid?: string;
    regions: string[];
  }>(response, 'enabled regions');
}

export async function mutateScanVaultEnabledRegions(
  method: 'POST' | 'PUT' | 'DELETE',
  input: { provider: string; accountUuid?: string; regions: string[] },
) {
  const request = {
    enabledRegionsRequest: {
      provider: input.provider,
      account_uuid: input.accountUuid,
      regions: input.regions,
    },
  };
  const response =
    method === 'POST'
      ? await ScanVaultService.Vulnerability.addEnabledRegions(request)
      : method === 'PUT'
      ? await ScanVaultService.Vulnerability.replaceEnabledRegions(request)
      : await ScanVaultService.Vulnerability.removeEnabledRegions(request);
  return readResponseData<{
    provider: string;
    account_uuid?: string;
    regions: string[];
  }>(response, 'enabled regions');
}

export async function updateScanVaultScannerSettings(
  input: Record<string, any>,
) {
  const source = input.scannerStatus ?? input;
  const response = await ScanVaultService.Vulnerability.updateScannerSettings({
    scannerSettingsRequest: {
      scan_frequency_hour:
        source.scan_frequency_hour ?? source.settings?.scan_frequency_hour ?? 0,
      skip_scanners:
        source.skip_scanners ?? source.settings?.skip_scanners ?? [],
      scan_snapshots:
        source.scan_snapshots ?? source.settings?.scan_snapshots ?? true,
      run_local: source.run_local ?? source.settings?.run_local ?? false,
      restart: source.restart ?? source.settings?.restart ?? false,
    },
  });
  return readResponseData(response, 'scanner settings');
}

/**
 * The generated 0.1.10 client owns ScanVault transport. These adapters keep
 * the stable legacy-shaped response models used by existing UI components.
 */
export async function getScanVaultResourceSummary(
  resourceUUID: string,
  input: Pick<ScanVaultListParams, 'status' | 'from' | 'to'> = {},
): Promise<VulnerabilityResourceInsightItem> {
  const response = await ScanVaultService.Vulnerability.getVulnerabilityResourceSummary(
    {
      resourceUuid: resourceUUID,
      status: input.status ?? 'open',
      from: input.from,
      to: input.to,
    },
  );
  return readResponseData<VulnerabilityResourceInsightItem>(
    response,
    'resource summary',
  );
}

export async function getScanVaultResourceFindings(
  resourceUUID: string,
  input: ScanVaultListParams = {},
): Promise<ScanVaultResourceFindingPage> {
  const values = (value?: string | string[]) =>
    value ? (Array.isArray(value) ? value : [value]) : undefined;
  const response = await ScanVaultService.Vulnerability.getVulnerabilityResourceFindings(
    {
      resourceUuid: resourceUUID,
      cursor: input.cursor,
      limit: input.limit,
      status: input.status,
      from: input.from,
      to: input.to,
      severity: values(input.severity),
      packageCategory: values(input.packageCategory),
      patchable: input.patchable ?? input.isPatchable,
      search: input.search ?? input.searchTerm,
    },
  );
  return readResponseData<ScanVaultResourceFindingPage>(
    response,
    'resource findings',
  );
}

export async function getScanVaultResourcePatchRecommendations(input: {
  resourceUuid: string;
  status?: 'open' | 'resolved';
  from?: string;
  to?: string;
  exploitable?: boolean;
  severity?: string | string[];
}) {
  const values = (value?: string | string[]) =>
    value ? (Array.isArray(value) ? value : [value]) : undefined;
  const response = await ScanVaultService.Vulnerability.getVulnerabilityPackages(
    {
      resourceUuid: [input.resourceUuid],
      status: input.status ?? 'open',
      from: input.from,
      to: input.to,
      patchable: true,
      exploitable: input.exploitable,
      severity: values(input.severity),
    },
  );
  const page = readResponseData<ScanVaultPage<ScanVaultPackage>>(
    response,
    'patch recommendations',
  );
  return {
    summary: {
      steps: [],
      disclaimer: 'Recommendations are based on the latest available scan.',
    },
    packages_to_patch: page.items.map(item => ({
      package_name: item.name,
      installed_version: item.version,
      package_category_name: item.ecosystem,
      vulnerability: {
        ...emptySeverityCountsForClient(),
        ...(item.severity_counts ?? {}),
      },
      cve_ids: item.cve_ids,
      patch_versions: item.fixed_versions ?? [],
      entities: {
        ...(item.provider_counts ?? {}),
        total: item.affected_resource_count,
      },
      is_patchable: item.patchable,
      last_seen: item.last_seen_at,
    })),
  };
}

function emptySeverityCountsForClient() {
  return { critical: 0, high: 0, medium: 0, low: 0, unknown: 0 };
}

export async function getScanVaultHostFacts(
  resourceUUID: string,
  input: { detector?: string; factType?: string; limit?: number } = {},
): Promise<ScanVaultPage<ScanVaultFact>> {
  const response = await ScanVaultService.Vulnerability.queryFacts({
    factKind: 'host_fact',
    factType: input.factType,
    detector: input.detector,
    resourceUuid: resourceUUID,
    limit: input.limit ?? 100,
  });
  return readResponseData<ScanVaultPage<ScanVaultFact>>(
    response,
    'resource facts',
  );
}

function flattenFact(fact: ScanVaultFact): Record<string, any> {
  const payload = fact.data?.payload;
  const values: Record<string, any> = {
    ...(fact.data ?? {}),
    resource_uuid: fact.resource_uuid,
    fact_type: fact.fact_type,
  };
  if (payload && typeof payload === 'object') {
    Object.entries(payload as Record<string, unknown>).forEach(
      ([key, value]) => {
        if (typeof value !== 'string') {
          values[key] = value;
          return;
        }
        const trimmedValue = value.trim();
        const isStructuredJson =
          trimmedValue.startsWith('{') || trimmedValue.startsWith('[');
        if (!isStructuredJson) {
          values[key] = value;
          return;
        }
        try {
          values[key] = JSON.parse(value);
        } catch {
          values[key] = value;
        }
      },
    );
  }
  return values;
}

/**
 * Translate normalized container observations to the legacy drawer shape.
 * ScanVault keeps resource identity and image metadata in the fact payload;
 * the adapter supplies the aliases used by the existing tables so the UI does
 * not need to know which producer emitted a particular field.
 */
export function mapScanVaultContainerFactToLegacy(fact: ScanVaultFact) {
  const value = flattenFact(fact);
  return {
    ...value,
    container_id: value.container_id ?? null,
    image: value.image ?? value.image_name,
    pod: value.pod ?? null,
    namespace: value.namespace ?? null,
    account_id: value.account_id ?? value.account_native_id ?? '',
    account_type: value.account_type ?? value.cloud ?? null,
    region: value.region ?? '',
    instance_id: value.instance_id ?? value.resource_id ?? null,
    library_vulnerabilities_count: value.library_vulnerabilities_count ?? null,
    os_vulnerabilities_count: value.os_vulnerabilities_count ?? null,
    resource_uuid: fact.resource_uuid,
    os_name: value.os_name ?? value.image_os,
    os_version: value.os_version ?? value.image_os_version ?? null,
  };
}

/** Maps normalized image metadata to the fields consumed by the ECR drawer. */
export function mapScanVaultImageMetadataToLegacy(fact?: ScanVaultFact) {
  if (!fact) return {};
  const value = flattenFact(fact);
  return {
    ...value,
    container_id: value.container_id ?? null,
    image_ref: value.image_ref ?? value.image,
    image_name: value.image_name ?? value.image,
    image_id: value.image_id ?? value.container_image_id,
    resolved_digest: value.resolved_digest ?? value.image_digest,
    image_source: value.image_source ?? value.source ?? null,
    image_os: value.image_os ?? value.os ?? null,
    image_architecture: value.image_architecture ?? value.architecture ?? null,
    image_created_at: value.image_created_at ?? value.created_at ?? null,
    image_workdir: value.image_workdir ?? value.workdir ?? null,
    image_user: value.image_user ?? value.user ?? null,
    image_entrypoint: value.image_entrypoint ?? value.entrypoint ?? null,
    image_cmd: value.image_cmd ?? value.cmd ?? null,
    image_exposed_ports:
      value.image_exposed_ports ?? value.exposed_ports ?? null,
    image_labels: value.image_labels ?? value.labels ?? null,
    os: value.os ?? value.image_os,
    architecture: value.architecture ?? value.image_architecture,
    created_at: value.created_at ?? value.image_created_at,
    workdir: value.workdir ?? value.image_workdir,
    user: value.user ?? value.image_user,
    entrypoint: value.entrypoint ?? value.image_entrypoint,
    cmd: value.cmd ?? value.image_cmd,
    exposed_ports: value.exposed_ports ?? value.image_exposed_ports,
    labels: value.labels ?? value.image_labels,
    dockerfile_hint: value.dockerfile_hint ?? value.image_dockerfile_hint,
  };
}

export async function getScanVaultContainerImageMetadata(
  resourceUUID: string,
  imageDigest?: string,
) {
  const response = await ScanVaultService.Vulnerability.getResourceContainerImageMetadata(
    {
      resourceUuid: resourceUUID,
      containerId: imageDigest,
    },
  );
  const page = readResponseData<ScanVaultPage<ScanVaultFact>>(
    response,
    'container image metadata',
  );
  return page.items.length
    ? mapScanVaultImageMetadataToLegacy(page.items[0])
    : {};
}

export async function getScanVaultContainers(
  resourceUUID: string,
  input: {
    containerIds?: string[];
    imageNames?: string[];
    repositoryUri?: string;
    imageDigest?: string;
    vulnerable?: boolean;
  } = {},
) {
  const response = await ScanVaultService.Vulnerability.getResourceContainers({
    resourceUuid: resourceUUID,
    containerId: input.containerIds,
    imageName: input.imageNames,
    repositoryUri: input.repositoryUri,
    imageDigest: input.imageDigest,
    vulnerable: input.vulnerable,
  });
  const page = readResponseData<ScanVaultPage<ScanVaultFact>>(
    response,
    'resource containers',
  );
  return page.items.map(mapScanVaultContainerFactToLegacy);
}

export async function getScanVaultContainersForImage(input: {
  imageNames?: string[];
  repositoryUri?: string;
  imageDigest?: string;
}) {
  const response = await ScanVaultService.Vulnerability.queryFacts({
    factKind: 'host_fact',
    factType: 'container_metadata',
    imageName: input.imageNames,
    repositoryUri: input.repositoryUri,
    imageDigest: input.imageDigest,
  });
  const page = readResponseData<ScanVaultPage<ScanVaultFact>>(
    response,
    'image containers',
  );
  return page.items.map(mapScanVaultContainerFactToLegacy);
}

function factValue(fact: ScanVaultFact, key: string): unknown {
  const payload = fact.data?.payload;
  if (payload && typeof payload === 'object') {
    const value = (payload as Record<string, unknown>)[key];
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }
  return fact.data?.[key];
}

function factTime(value: unknown): string | undefined {
  if (typeof value === 'number') {
    const date = new Date(value > 10_000_000_000 ? value : value * 1000);
    return Number.isNaN(date.valueOf()) ? undefined : date.toISOString();
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? value : date.toISOString();
  }
  return undefined;
}

/** Maps normalized host observations to the small activity contract used by the UI. */
export async function getScanVaultLoginActivity(
  resourceUUID: string,
  activityType: 'success' | 'failed' | 'vm_login',
): Promise<ScanVaultLoginActivity[]> {
  if (activityType === 'vm_login') return [];
  const factTypes =
    activityType === 'success'
      ? ['last']
      : ['ssh_external_failures', 'ssh_internal_failures'];
  const pages = await Promise.all(
    factTypes.map(factType =>
      getScanVaultHostFacts(resourceUUID, {
        detector: factType === 'last' ? 'osquery' : 'ssh',
        factType,
        limit: 100,
      }),
    ),
  );
  const facts = pages.flatMap(page => page.items);
  if (activityType === 'success') {
    return facts.map(fact => ({
      username: fact.data?.principal as string,
      host: factValue(fact, 'host') as string,
      login_time: factTime(factValue(fact, 'time')) ?? fact.first_seen_at,
      logout_time: undefined,
    }));
  }
  return facts.map(fact => ({
    username: (fact.data?.principal as string) || '*',
    host:
      (factValue(fact, 'ip') as string) ||
      (fact.fact_type === 'ssh_external_failures' ? '0.0.0.0/0' : undefined),
    number_of_attempts:
      fact.fact_type === 'ssh_external_failures'
        ? Number(factValue(fact, 'failed_logins') ?? 0)
        : 1,
    last_attempt_time: factTime(factValue(fact, 'time')) ?? fact.last_seen_at,
  }));
}

export async function getScanVaultSecrets(resourceUUID: string) {
  const response = await ScanVaultService.Vulnerability.queryFindings({
    resourceUuid: [resourceUUID],
    detector: ['secrets'],
    limit: 100,
  });
  const page = readResponseData<{
    items: Array<{
      location: string;
      rule_id: string;
      severity: string;
      title: string;
    }>;
  }>(response, 'resource secrets');
  return {
    secrets: page.items.map(item => ({
      target: item.location,
      rule_id: item.rule_id,
      severity: item.severity,
      title: item.title,
      start_line: undefined,
      end_line: undefined,
    })),
  };
}

export function mapScanVaultResourceSummaryToLegacy(
  item: VulnerabilityResourceInsightItem,
) {
  const counts = item.vulnerabilities ?? {};
  const vulnerability = {
    critical: counts.critical ?? 0,
    high: counts.high ?? 0,
    medium: counts.medium ?? 0,
    low: counts.low ?? 0,
    unknown: counts.unknown ?? 0,
  };
  const total = Object.values(vulnerability).reduce(
    (sum, value) => sum + Number(value ?? 0),
    0,
  );
  const summary = {
    total,
    available_patches: 0,
    vulnerability,
  };
  const attrs = item.attrs ?? {};
  return {
    name: attrs.os_name ?? item.name,
    version: attrs.os_version,
    platform: attrs.platform,
    platform_like: attrs.platform_like,
    codename: attrs.codename,
    arch: attrs.architecture ?? attrs.arch,
    os_vulnerability: summary,
    library_vulnerability: summary,
    applications: summary,
  };
}

export function mapScanVaultFindingsToLegacy(
  page: ScanVaultResourceFindingPage,
  pageNumber = 1,
  pageSize = page.items.length || 100,
  sortBy = 'severity',
  sortOrder: 'asc' | 'desc' = 'desc',
) {
  const severityOrder: Record<string, number> = {
    critical: 5,
    high: 4,
    medium: 3,
    low: 2,
    unknown: 1,
  };
  const items = page.items.map(item => ({
    cve_id: item.rule_id,
    cvss_v3_score: (item as any).cvss_score,
    cvss_v3_vector: (item as any).cvss_vector,
    severity: item.severity.toUpperCase(),
    title: item.title,
    description: (item as any).description,
    package_name: item.package_name,
    installed_version: item.installed_version,
    fixed_in_version: item.fixed_version,
    exploitable: item.exploitable,
    is_patchable: item.patchable,
    entities: { total: 1 },
  }));

  if (sortBy && sortOrder) {
    const direction = sortOrder === 'desc' ? -1 : 1;
    const valueFor = (item: typeof items[number]) => {
      if (sortBy === 'severity')
        return severityOrder[item.severity.toLowerCase()] ?? 0;
      if (sortBy === 'cvss_score' || sortBy === 'cvss_v3.score') {
        return item.cvss_v3_score ?? -1;
      }
      return (item as any)[sortBy] ?? '';
    };
    items.sort((left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }
      return String(leftValue).localeCompare(String(rightValue)) * direction;
    });
  }

  return {
    items,
    // Scan Vault uses cursor pagination and reports no exact total. Keep the
    // legacy numbered pager enabled while another cursor is available.
    total:
      page.total_count ??
      (page.next_cursor
        ? pageNumber * pageSize + 1
        : (pageNumber - 1) * pageSize + page.items.length),
    page: pageNumber,
    size: pageSize,
  };
}

interface ScanVaultListParams extends VulnerabilityDashboardParams {
  cursor?: string;
  limit?: number;
  patchable?: boolean;
  severity?: string | string[];
  search?: string;
  packageCategory?: string | string[];
  ecosystem?: string | string[];
  knownExploited?: boolean;
  exploitable?: boolean;
  isPatchable?: boolean;
  searchTerm?: string;
  status?: 'open' | 'resolved';
  detector?: string | string[];
}

function readResponseData<T>(
  response: { data?: unknown } | undefined,
  label: string,
): T {
  // The generated Axios client already unwraps the HTTP response envelope to
  // `response.data`; ScanVault's API models are not wrapped in another `data`
  // property.
  const data = response?.data;
  if (data === undefined || data === null || typeof data !== 'object') {
    throw new Error(`Vulnerability ${label} data is temporarily unavailable`);
  }
  return data as T;
}

/**
 * The generated client owns URL construction, authentication, and transport.
 * This adapter keeps transport details out of page components.
 */
export async function getVulnerabilityDashboard(
  input: VulnerabilityDashboardParams = {},
): Promise<VulnerabilityDashboardResponse> {
  const response = await ScanVaultService.Vulnerability.getVulnerabilityDashboard(
    toDashboardRequest(input),
  );

  return readResponseData<VulnerabilityDashboardResponse>(
    response,
    'dashboard',
  );
}

const scanVaultImageCursorChains = new Map<string, Map<number, string>>();

function scanVaultImageFilterKey(input: Record<string, any>) {
  return JSON.stringify(
    Object.keys(input)
      .filter(key => !['page', 'cursor'].includes(key))
      .sort()
      .map(key => [key, input[key]]),
  );
}

function scanVaultImageCursorForPage(input: Record<string, any>, page: number) {
  if (input.cursor || page <= 1) return input.cursor;
  return scanVaultImageCursorChains
    .get(scanVaultImageFilterKey(input))
    ?.get(page - 1);
}

function rememberScanVaultImageCursor(
  input: Record<string, any>,
  page: number,
  nextCursor?: string,
) {
  const key = scanVaultImageFilterKey(input);
  const chain =
    scanVaultImageCursorChains.get(key) ?? new Map<number, string>();
  if (nextCursor) {
    chain.set(page, nextCursor);
  } else {
    chain.delete(page);
  }
  scanVaultImageCursorChains.set(key, chain);
}

export function mapScanVaultVulnerableImageToLegacy(
  item: VulnerabilityImageBucket,
  input: Record<string, any> = {},
) {
  const image = item.image;
  const repositoryUri = item.repository_uri ?? '';
  const repositoryName = repositoryUri
    ? repositoryUri.slice(repositoryUri.lastIndexOf('/') + 1)
    : image.split('/').pop()?.split('@')[0]?.split(':')[0] ?? image;
  const severityCounts = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    unknown: 0,
    ...(item.severity_counts ?? {}),
  };
  const vulnerabilityTotal = Object.values(severityCounts).reduce(
    (total, count) => total + Number(count ?? 0),
    0,
  );
  return {
    ...item,
    id: item.image_digest ?? image,
    name: image,
    image,
    uri: image,
    image_tags: image ? [image] : [],
    image_digest: item.image_digest ?? '',
    repository_name: repositoryName,
    repository_uri: repositoryUri,
    registry_id: repositoryUri.split('/')[0] ?? '',
    registry_uuid: input.repositoryUuid,
    environment_id: input.environmentId,
    account_uuid: Array.isArray(input.accountId)
      ? input.accountId[0]
      : input.accountId,
    image_scan_status: input.resolved ? 'resolved' : 'open',
    vulnerabilities: {
      vulnerability: severityCounts,
      total: vulnerabilityTotal,
    },
  };
}

export async function getScanVaultVulnerableImages(
  input: Record<string, any> = {},
) {
  const environmentId = input.environmentId;
  const accountIds = input.accountId
    ? Array.isArray(input.accountId)
      ? input.accountId
      : [input.accountId]
    : [];
  const pageSize = Number(input.pageSize ?? input.limit ?? 100);
  const resourceIds = input.repositoryUuid
    ? Array.isArray(input.repositoryUuid)
      ? input.repositoryUuid
      : [input.repositoryUuid]
    : [];
  const resourceTypes = input.resourceTypes ?? [];
  const packageCategory = !input.repositoryUuid
    ? String(input.detectionMethod ?? 'os').toLowerCase()
    : undefined;
  const patchable = toOptionalBoolean(input.isPatchable);
  const severity = input.severity
    ? String(input.severity).toLowerCase()
    : undefined;
  const status: 'open' | 'resolved' | undefined =
    input.resolved === undefined
      ? undefined
      : input.resolved
      ? 'resolved'
      : 'open';
  const requestedPageValue = Number(input.page ?? 1);
  const page =
    Number.isInteger(requestedPageValue) && requestedPageValue > 0
      ? requestedPageValue
      : 1;
  let cursor = scanVaultImageCursorForPage(input, page);
  let response: ScanVaultPage<VulnerabilityImageBucket> = {
    items: [],
    total_count: 0,
    count_mode: 'exact',
  };
  const firstPage = input.cursor ? page : 1;
  for (let currentPage = firstPage; currentPage <= page; currentPage += 1) {
    const pageResponse = await ScanVaultService.Vulnerability.getVulnerabilityImages(
      {
        environmentId: environmentId ? [environmentId] : undefined,
        accountUuid: accountIds,
        resourceType: resourceTypes,
        resourceUuid: resourceIds,
        packageCategory,
        patchable,
        severity,
        search: input.searchTerm ? String(input.searchTerm) : undefined,
        limit: input.pageSize ?? input.limit ? pageSize : undefined,
        cursor,
        status,
        from: input.effectiveStart,
        to: input.resolvedDate,
      },
    );
    response = readResponseData<ScanVaultPage<VulnerabilityImageBucket>>(
      pageResponse,
      'vulnerable images',
    );
    rememberScanVaultImageCursor(input, currentPage, response.next_cursor);
    if (currentPage === page) break;
    if (!response.next_cursor) {
      response = { ...response, items: [], next_cursor: undefined };
      break;
    }
    cursor = response.next_cursor;
  }
  const items = response.items ?? [];
  const lowerBoundTotal = Math.max(
    (page - 1) * pageSize + items.length + (response.next_cursor ? 1 : 0),
    items.length,
  );
  return {
    items: items.map(item => mapScanVaultVulnerableImageToLegacy(item, input)),
    page,
    page_size: pageSize,
    total: response.total_count ?? lowerBoundTotal,
    next_cursor: response.next_cursor,
  };
}

export async function getVulnerabilityPrioritization(
  input: VulnerabilityDashboardParams = {},
): Promise<VulnerabilityPrioritization> {
  const client = (ScanVaultService.Vulnerability as unknown) as {
    getVulnerabilityPrioritization: (request: {
      environmentId?: string[];
      accountUuid?: string[];
      resourceType?: string[];
      steps?: Set<string>;
    }) => Promise<{ data?: unknown }>;
  };
  if (typeof client.getVulnerabilityPrioritization !== 'function') {
    throw new Error('Vulnerability prioritization is temporarily unavailable');
  }
  const response = await client.getVulnerabilityPrioritization(
    toPrioritizationRequest(input),
  );
  return readResponseData<VulnerabilityPrioritization>(
    response,
    'prioritization',
  );
}

export async function getScanVaultFindings(input: ScanVaultListParams = {}) {
  const request = toListRequest(input);
  const response = await ScanVaultService.Vulnerability.getVulnerabilityFindings(
    request,
  );

  return readResponseData<ScanVaultPage<ScanVaultFinding>>(
    response,
    'findings',
  );
}

export async function getScanVaultCVEs(input: ScanVaultListParams = {}) {
  const request = toListRequest(input);
  const response = await ScanVaultService.Vulnerability.getVulnerabilityCVEs(
    request,
  );

  return readResponseData<ScanVaultPage<ScanVaultCVE>>(response, 'CVE catalog');
}

export async function getScanVaultCVEDetails(input: {
  cveIds?: string[];
  packageName?: string;
  packageVersion?: string;
  search?: string;
  severity?: string | string[];
  status?: 'open' | 'resolved';
  packageCategory?: string | string[];
}) {
  const values = (value?: string | string[]) =>
    value ? (Array.isArray(value) ? value : [value]) : undefined;
  const response = await ScanVaultService.Vulnerability.getVulnerabilityCVEs({
    cveId: input.cveIds,
    packageName: input.packageName ? [input.packageName] : undefined,
    packageVersion: input.packageVersion ? [input.packageVersion] : undefined,
    search: input.search,
    status: input.status,
    severity: values(input.severity),
    limit: 100,
  });
  const page = readResponseData<
    ScanVaultPage<
      ScanVaultCVE & {
        cvss?: Record<string, string>;
        title?: string;
        description?: string;
        remediation?: string;
        first_seen_at?: string;
      }
    >
  >(response, 'CVE details');
  return {
    ...page,
    items: page.items.map(item => ({
      ...item,
      vulnerability_id: item.cve_id,
      cvss_v3_score: item.cvss_score,
      cvss_v3_vector: scanVaultCvssV3(item).vector,
      cvss_v3: scanVaultCvssV3(item),
      fixed_in_version: item.fixed_version,
      installed_version: item.package_version,
      is_patchable: item.patchable,
      exploitable: item.exploitable,
      first_seen_at: item.first_seen_at ?? item.last_seen_at,
    })),
  };
}

export async function getScanVaultPackages(input: ScanVaultListParams = {}) {
  const request = toListRequest(input);
  const response = await ScanVaultService.Vulnerability.getVulnerabilityPackages(
    request,
  );

  return readResponseData<ScanVaultPage<ScanVaultPackage>>(
    response,
    'package catalog',
  );
}

function toDashboardRequest(input: VulnerabilityDashboardParams) {
  const accounts = input.accountUuid
    ? [input.accountUuid]
    : input.accountId
    ? Array.isArray(input.accountId)
      ? input.accountId
      : [input.accountId]
    : undefined;
  const from = input.from ?? dateBoundary(input.startDate, false);
  const to = input.to ?? dateBoundary(input.endDate, true);
  return {
    environmentId: input.environmentId ? [input.environmentId] : undefined,
    accountUuid: accounts,
    resourceType: input.resourceTypes,
    from,
    to,
  };
}

function toPrioritizationRequest(input: VulnerabilityDashboardParams) {
  return {
    environmentId: input.environmentId ? [input.environmentId] : undefined,
    accountUuid: input.accountUuid
      ? [input.accountUuid]
      : input.accountId
      ? Array.isArray(input.accountId)
        ? input.accountId
        : [input.accountId]
      : undefined,
    resourceType: input.resourceTypes,
    steps: input.steps?.length ? new Set(input.steps) : undefined,
  };
}

function toListRequest(input: ScanVaultListParams) {
  const values = (value?: string | string[]) =>
    value ? (Array.isArray(value) ? value : [value]) : undefined;
  return {
    ...toDashboardRequest(input),
    cursor: input.cursor,
    limit: input.limit,
    patchable: input.patchable ?? input.isPatchable,
    knownExploited: input.knownExploited,
    exploitable: input.exploitable,
    // ScanVault stores severities in lowercase; UI options are uppercase.
    severity: values(input.severity)?.map(value => value.toLowerCase()),
    search: input.search ?? input.searchTerm,
    ecosystem: values(input.ecosystem),
    status:
      input.status ??
      ((input as any).resolved === true ? 'resolved' : undefined),
  };
}

function dateBoundary(value: string | undefined, endExclusive: boolean) {
  if (!value) return undefined;
  if (value.includes('T')) return value;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (endExclusive) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
}

function toOptionalBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
}
